var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { useFetchPromise } from '@rad/hooks';
import { getConst, withDone, withValidate } from '@rad/utility/http';
import { useToken } from '../../../utility/general';
import { terminalName, fetchDejavooTerminals, storeDejavooTerminal, setDefaultDejavooTerminal, deleteDejavooTerminal, checkDejavooTerminalStatus } from '../../../http/dejavoo';
import Card from '../../components/card';
import Button from '../../components/button';
import CInput from '../../components/input';
import CModal from '../../components/modal';
import CModalConfirm from '../../components/modal.confirm';
const emptyForm = {
    'display_name': '',
    'tpn_number': '',
    'auth_key': '',
    'ecom_auth_key': '',
    'register_id': '',
    'label': '',
    'api_key': '',
    'secret_key': '',
    'copy_keys': false,
};
export default (props) => {
    var _a, _b;
    const token = useToken();
    const merchantId = props.merchantId || ((_a = token === null || token === void 0 ? void 0 : token.merchant) === null || _a === void 0 ? void 0 : _a.id) || null;
    const [terminals, reloadTerminals] = useFetchPromise(merchantId ? fetchDejavooTerminals(merchantId) : getConst(undefined), [merchantId]);
    const [modal, setModal] = React.useState(null);
    const [form, setForm] = React.useState(emptyForm);
    const [errors, setErrors] = React.useState(null);
    const [confirmDelete, setConfirmDelete] = React.useState(null);
    const [statuses, setStatuses] = React.useState({});
    const [checkingStatus, setCheckingStatus] = React.useState(null);
    // Tell other tabs/screens the config changed. A throwaway channel avoids holding one open across
    // React's mount/cleanup cycles, which would close it and break a later post.
    const notifyConfigUpdated = () => {
        const channel = new BroadcastChannel('DEJAVOO');
        channel.postMessage('CONFIG_UPDATED');
        channel.close();
    };
    React.useEffect(() => {
        const channel = new BroadcastChannel('DEJAVOO');
        channel.onmessage = () => { reloadTerminals(); };
        return () => { channel.close(); };
    }, []);
    const defaultTerminal = terminals === null || terminals === void 0 ? void 0 : terminals.find((t) => t.is_default);
    const canCopyKeys = !!(defaultTerminal === null || defaultTerminal === void 0 ? void 0 : defaultTerminal.has_batch_keys) && (modal === null || modal === void 0 ? void 0 : modal.terminal) === null;
    const openAdd = () => {
        setErrors(null);
        setForm(emptyForm);
        setModal({ 'terminal': null });
    };
    const openEdit = (t) => {
        var _a;
        setErrors(null);
        setForm({
            'display_name': t.display_name,
            'tpn_number': String(t.tpn_number),
            'auth_key': t.auth_key,
            'ecom_auth_key': t.ecom_auth_key,
            'register_id': t.register_id,
            'label': (_a = t.label) !== null && _a !== void 0 ? _a : '',
            'api_key': '',
            'secret_key': '',
            'copy_keys': false,
        });
        setModal({ 'terminal': t });
    };
    const save = (finish) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        if (!merchantId) {
            finish();
            return;
        }
        const result = yield withDone(withValidate(storeDejavooTerminal(merchantId, {
            'id': (_b = (_a = modal === null || modal === void 0 ? void 0 : modal.terminal) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null,
            'copy_keys_from': form.copy_keys && defaultTerminal ? defaultTerminal.id : null,
            'tpn_number': parseInt(form.tpn_number, 10),
            'auth_key': form.auth_key,
            'ecom_auth_key': form.ecom_auth_key,
            'register_id': form.register_id,
            'display_name': form.display_name,
            'label': form.label.trim() || null,
            'api_key': form.api_key || null,
            'secret_key': form.secret_key || null,
        })))();
        finish();
        if (result[0]) {
            setModal(null);
            notifyConfigUpdated();
            reloadTerminals();
        }
        else {
            setErrors(Object.fromEntries(result[1].map((x) => [x.field, x.message])));
        }
    });
    const remove = (finish) => __awaiter(void 0, void 0, void 0, function* () {
        if (!merchantId || !confirmDelete) {
            finish();
            return;
        }
        yield withDone(deleteDejavooTerminal(merchantId, confirmDelete.id))();
        finish();
        setConfirmDelete(null);
        notifyConfigUpdated();
        reloadTerminals();
    });
    const makeDefault = (t) => __awaiter(void 0, void 0, void 0, function* () {
        if (!merchantId)
            return;
        yield withDone(setDefaultDejavooTerminal(merchantId, t.id))();
        notifyConfigUpdated();
        reloadTerminals();
    });
    const checkStatus = (t) => __awaiter(void 0, void 0, void 0, function* () {
        if (!merchantId)
            return;
        setCheckingStatus(t.id);
        const status = yield withDone(checkDejavooTerminalStatus(merchantId, t.id))();
        setCheckingStatus(null);
        setStatuses((s) => (Object.assign(Object.assign({}, s), { [t.id]: status })));
        window.setTimeout(() => setStatuses((s) => Object.fromEntries(Object.entries(s).filter(([id]) => id !== t.id))), 5000);
    });
    const field = (name, label, type = 'text', required = true) => _jsx(CInput, { type: type, value: form[name], onChange: (x) => setForm((f) => (Object.assign(Object.assign({}, f), { [name]: x }))), required: required, error: errors === null || errors === void 0 ? void 0 : errors[name], children: label });
    return _jsxs(_Fragment, { children: [_jsxs(CModalConfirm, { title: "Remove terminal", action: "Remove", visible: !!confirmDelete, onClose: () => setConfirmDelete(null), onSubmit: remove, children: ["Remove ", confirmDelete ? terminalName(confirmDelete) : 'this terminal', "? Existing data will not be affected, but new payments cannot be sent to it."] }), _jsxs(CModal, { title: (modal === null || modal === void 0 ? void 0 : modal.terminal) ? `Edit ${terminalName(modal.terminal)}` : 'Add terminal', action: "Save", visible: modal, onClose: () => setModal(null), onSubmit: (finish) => save(finish), children: [field('label', 'Label (e.g. Front counter)', 'text', false), field('display_name', 'Display Name'), field('tpn_number', 'TPN Number'), field('auth_key', 'AuthKey'), field('ecom_auth_key', 'Ecom AuthKey'), field('register_id', 'Register ID'), ((_b = modal === null || modal === void 0 ? void 0 : modal.terminal) === null || _b === void 0 ? void 0 : _b.has_batch_keys) && !form.api_key && !form.secret_key &&
                        _jsx("div", { className: "alert alert-light py-2", children: "Batch reporting keys are set. Enter new values below to replace them." }), canCopyKeys &&
                        _jsxs("div", { className: "form-check mb-2", children: [_jsx("input", { className: "form-check-input", type: "checkbox", id: "dejavoo-copy-keys", checked: form.copy_keys, onChange: (e) => setForm((f) => (Object.assign(Object.assign({}, f), { 'copy_keys': e.target.checked }))) }), _jsxs("label", { className: "form-check-label", htmlFor: "dejavoo-copy-keys", children: ["Use the batch reporting keys from ", defaultTerminal ? terminalName(defaultTerminal) : 'the default terminal'] })] }), !form.copy_keys && _jsxs(_Fragment, { children: [field('api_key', 'API Key (batch reporting, optional)', 'password', false), field('secret_key', 'Secret Key (batch reporting, optional)', 'password', false)] })] }), _jsx(Card, { title: "Dejavoo SPin", children: terminals === undefined
                    ? _jsx(_Fragment, { children: "Loading..." })
                    : terminals.length === 0
                        ? _jsx(Button, { color: "primary", onClick: openAdd, w100: true, mb: 2, children: "Connect Dejavoo SPin" })
                        : _jsxs(_Fragment, { children: [_jsxs("div", { className: "d-flex justify-content-between align-items-center mb-2", children: [_jsx("h6", { className: "mb-0", children: "Terminals" }), _jsx(Button, { color: "primary", onClick: openAdd, children: "Add terminal" })] }), terminals.map((t) => _jsxs("div", { className: "border rounded p-2 mb-2", children: [_jsxs("div", { className: "d-flex align-items-center justify-content-between flex-wrap gap-2", children: [_jsxs("div", { className: "me-2", children: [_jsxs("div", { className: "d-flex align-items-center", children: [_jsx("span", { className: "fw-semibold", children: terminalName(t) }), t.is_default && _jsx("span", { className: "badge text-bg-primary ms-2", children: "Default" })] }), !!t.label && _jsxs("div", { className: "text-muted small", children: ["TPN ", t.tpn_number] })] }), _jsxs("div", { className: "d-flex flex-shrink-0", children: [_jsx(Button, { color: "secondary", me: 1, onClick: () => checkStatus(t), disabled: checkingStatus === t.id, children: checkingStatus === t.id ? 'Checking...' : 'Status' }), !t.is_default && _jsx(Button, { color: "secondary", me: 1, onClick: () => makeDefault(t), children: "Make default" }), _jsx(Button, { color: "secondary", me: 1, onClick: () => openEdit(t), children: "Edit" }), _jsx(Button, { color: "danger", onClick: () => setConfirmDelete(t), children: "Remove" })] })] }), statuses[t.id] &&
                                            _jsx("div", { className: `alert mb-0 mt-2 py-1 ${statuses[t.id].connected ? 'alert-success' : 'alert-danger'}`, children: statuses[t.id].message })] }, t.id))] }) })] });
};
